/** @file Draw Order panel body: slots front-first, drag to reorder, step buttons, keys while animating. */
import { useState } from 'react';
import { useStore } from 'zustand';
import { selectionStore } from '../../stores/selectionStore';
import { updateSelection } from '../../stores/updateSelection';
import { joinClass } from '../../ui-kit/logic/joinClass';
import type { RigView } from '../rigKit/rigView';
import { TargetLine } from '../rigKit/TargetLine';
import { DrawOrderSteps } from './DrawOrderSteps';
import { orderMove, type OrderMove } from './orderMove';
import { reorderCall } from './reorderCall';
import { shownOrder } from './shownOrder';

/** The selection is the editor's slot selection (shared with the viewport): the last live selected slot is the active one,
 *  as in the Inspector. A drop or a step is one command.
 *  dragstart sets text/plain data because Firefox starts no drag without it; drop cancels the default
 *  action, or Firefox navigates to that text. */
export function DrawOrderView({ doc, t, run, target }: RigView) {
  const picked = useStore(selectionStore, (s) => s.slots);
  const selected = picked.findLast((id) => Object.hasOwn(doc.slots, id)) ?? null;
  const [dragging, setDragging] = useState<string | null>(null);
  const { order, keyHere } = shownOrder(doc, target);
  const move = (m: OrderMove | null): void => { if (m !== null) run([reorderCall(target, order, m)]); };
  const dropOn = (id: string): void => { if (dragging !== null) move(orderMove.drop(order, dragging, id)); setDragging(null); };
  return (
    <div className="rig-body">
      <TargetLine t={t} target={target} {...(keyHere ? { extra: t('panels.drawOrder.keyed') } : {})} />
      <ul className="rig-list-rows" aria-label={t('panels.drawOrder.list')}>
        {[...order].reverse().map((id) => (
          <li key={id} draggable className={joinClass('rig-list-row', id === selected && 'rig-list-row-selected', id === dragging && 'rig-list-row-dragging')}
            onDragStart={(e) => { e.dataTransfer.setData('text/plain', id); setDragging(id); }} onDragEnd={() => { setDragging(null); }} onDragOver={(e) => { e.preventDefault(); }} onDrop={(e) => { e.preventDefault(); dropOn(id); }}>
            <button type="button" aria-pressed={id === selected} onClick={() => { updateSelection({ bones: [], slots: [id] }, 'replace'); }}>{doc.slots[id]?.name ?? id}</button>
          </li>
        ))}
      </ul>
      <DrawOrderSteps t={t} order={order} selected={selected} onMove={move}
        onClearKey={target.kind === 'key' && keyHere ? () => { run([{ name: 'animation.deleteKeys', input: { animation: target.animation, channel: 'drawOrder', frames: [target.frame] } }]); } : undefined} />
    </div>
  );
}
